import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataContext } from "../../context/Data.context";
import { Separator } from "@moreirapontocom/npmhelpers";
import { _generateHash } from "../../services/helpers";
import Device from "../../components/Device/Device.component";
import dayjs from "dayjs";

import "./NewTest.scss";

const NewTest = () => {
  const [step, setStep] = useState(1); // DEFAULT IS 1
  const navigate: any = useNavigate();

  const { data, setData } = useContext(DataContext);

  useEffect(() => {
    setData({
      ...data,
      pageHeader: {
        title: "New Test",
        backButton: true,
        previousPage: "/home",
      },
    });

    _getReaders();
  }, []);

  const [readers, setReaders] = useState([] as any[]);
  const _getReaders = () => {
    const storageKey = "readers";
    const readersStorage: any = localStorage.getItem(storageKey);
    if (readersStorage) {
      const readers: any = JSON.parse(readersStorage);
      setReaders(readers);
      return;
    }
  };

  const [selectedReader, setSelectedReader] = useState({} as any);
  const [selectedSensorType, setSelectedSensorType] = useState("" as string);

  const _startTimer = (durationMinutes: number) => {
    const durationSeconds = durationMinutes * 60;
    let elapsedSeconds = 0;
  
    const intervalId = setInterval(() => {
      if (elapsedSeconds < durationSeconds) {
        elapsedSeconds++;
        setTestProgress(Math.round((elapsedSeconds / durationSeconds) * 100));
      } else {
        clearInterval(intervalId);
      }
    }, 1000);
  }

  const [testProgress, setTestProgress] = useState(0);
  const [resultHash, setResultHash] = useState(null as any);
  useEffect(() => {

    // STEP 5 - Progress bar animation
    if (step === 5) {

      // Hide back button
      setData({
        ...data,
        pageHeader: {
          ...data.pageHeader,
          title: "Test in Progress...",
          backButton: true,
          previousPage: "/home",
        },
        hideNavbar: false,
      });

      /**
       * When the test starts, create the test result page without the result.
       * This allows user to close the progress page and come back later to see the result.
       */
      const testTimestamp: any = dayjs();

      // Create a result ID
      const operationHash: string = JSON.stringify({
        readerId: selectedReader.id,
        sensorType: selectedSensorType,
        timestamp: testTimestamp.format("YYYYMMDDHHmmss")
      });
      const theResultHash: string = _generateHash(operationHash);
      setResultHash(theResultHash);

      // Add test result to local storage
      const storageKey = "results";
      const testsStorage: any = localStorage.getItem(storageKey);
      let tests: any = [];
      if (testsStorage) {
        tests = JSON.parse(testsStorage);
      }

      tests.push({
        id: theResultHash,
        readerId: selectedReader.id,
        readerSerialNumber: selectedReader.serialNumber,
        result: "",
        timestamp: testTimestamp.format("YYYY-MM-DD HH:mm:ss"),
        sensorType: selectedSensorType,
        reported: false,
        isRunning: true,
      });
      localStorage.setItem(storageKey, JSON.stringify(tests));

      _startTimer(1);
    }

    // STEP 6 - Test result
    if (step === 6) {

      // Hide back button
      setData({
        ...data,
        pageHeader: {
          ...data.pageHeader,
          title: "Test Result",
          backButton: false,
        },
      });

      // Update test result in local storage
      const storageKey = "results";
      const testsStorage: any = localStorage.getItem(storageKey);
      let tests: any = [];
      if (testsStorage) {
        tests = JSON.parse(testsStorage);
      }

      const newTests: any = tests.map((test: any) => {
        if (test.id === resultHash) {
          test = {
            ...test,
            isRunning: false,
            result: "negative",
          };
        }
        return test;
      });
      localStorage.setItem(storageKey, JSON.stringify(newTests));

      navigate(`/result/${resultHash}`);
      return;
    }
  }, [step]);

  /**
   * The current device is not working properly, so user need to click twice to send messages.
   * This recursion hack is to bypass this problem.
   * When this is fixed by our engineering team, remove it.
   */

  /*
  const _sendMessage = async (command: any, count = 0) => {
    if (count >= 3) {
      return;
    }

    console.log("Sent message", count);
    if (data.selectedBluetoothDevice) {
      const response: any = await sendMessageToDevice(command, data.selectedBluetoothDevice);
      console.log("RESPONSE sendMessageToDevice", response);
    }

    _sendMessage(command, count + 1);
  };
  */

  return <>
    {/*
    <pre>
      {JSON.stringify(data.selectedBluetoothDevice, null, 2)}
    </pre>
    */}

    {/* STEP 1 - SELECT READER */}
    {step === 1 && <>
      {readers.length === 0 && <>
        <div className="text-center">
          <strong>No readers found</strong>
        </div>
      </>}

      {readers.length > 0 && <>
        <div className="text-center text-muted">
          <small>
            Select a reader to start a new test
          </small>
        </div>

        <Separator size={10} />

        <ul className="list-group">
          {readers.map((reader: any, index: number) => 
            <React.Fragment key={`readers-${reader.id}-${index}`}>
              <Device
                reader={reader}
                action="select_to_pair"
                isSelected={selectedReader.id === reader.id}
                onClick={() => setSelectedReader(reader)} />
              </React.Fragment>
            )}
        </ul>
      </>}

      <Separator size={20} />

      <div className="row text-center justify-content-center">
        <div className="col-5">
          <Link to={"/pairReader"} className="btn btn-outline-secondary btn-sm">
            <i className="fas fa-plus me-2"></i> New Reader
          </Link>
        </div>
      </div>

      <Separator size={60} />

      <div className="d-grid">
        <button
          onClick={() => setStep(2)}
          disabled={!selectedReader.id}
          type="button"
          className="btn btn-primary btn-featured">
            Next
        </button>
      </div>
    </>}


    {/* STEP 2 - SELECT SENSOR TYPE */}
    {step === 2 && <>
      <div className="text-center text-muted">
        <small>
          Select a sensor type
        </small>
      </div>

      <Separator size={10} />

      <ul className="list-group">
        <li className={`list-group-item cursor-pointer ${selectedSensorType === "covid19" && "selected"}`} onClick={() => setSelectedSensorType("covid19")}>
          <div className="row align-items-center">
            <div className="col-3">
              <img src="images/sensor-covid19.png" alt="" className="img-fluid" />
            </div>
            <div className="col">
              <strong>COVID 19 Sensor</strong>
            </div>
          </div>
        </li>
        <li className={`list-group-item cursor-pointer ${selectedSensorType === "covidflu" && "selected"}`} onClick={() => setSelectedSensorType("covidflu")}>
          <div className="row align-items-center">
            <div className="col-3">
              <img src="images/sensor-covid-flu.png" alt="" className="img-fluid" />
            </div>
            <div className="col">
              <strong>COVID / FLU Sensor</strong>
            </div>
          </div>
        </li>
      </ul>

      <Separator size={60} />

      <div className="d-grid">
        <button
          onClick={() => setStep(3)}
          disabled={selectedSensorType === ""}
          type="button"
          className="btn btn-primary btn-featured">
            Next
        </button>
      </div>
    </>}


    {/* STEP 3 - INSTRUCTIONS */}
    {step === 3 && <>
      <img src="images/test-instructions.png" alt="" className="img-fluid" />

      <Separator size={60} />

      <div className="d-grid">
        <button onClick={() => setStep(4)} type="button" className="btn btn-primary btn-featured">
          Next
        </button>
      </div>

      <Separator size={160} />
    </>}


    {/* STEP 4 - START TEST */}
    {step === 4 && <>
      <img src="images/test-instructions-run.png" alt="" className="img-fluid" />

      <Separator size={60} />

      {/*
      <button onClick={() => _sendMessage("CMD08*")} type="button" className="btn btn-primary btn-featured">
        sendMessage "HELLO" 08
      </button>

      <button onClick={() => _sendMessage("CMD09*")} type="button" className="btn btn-primary btn-featured">
        sendMessage "ENTER TEST MODE" 09
      </button>

      <button onClick={() => _sendMessage("CMD06*")} type="button" className="btn btn-primary btn-featured">
        sendMessage "TEST RESULT" 06
      </button>

      <button onClick={() => _sendMessage("CMD10*")} type="button" className="btn btn-primary btn-featured">
        sendMessage "META DATA" 10
      </button>
      */}

      <div className="d-grid">
        <button onClick={() => setStep(5)} type="button" className="btn btn-primary btn-featured">
          Run Test
        </button>
      </div>

      <Separator size={160} />
    </>}


    {/* STEP 5 - TEST IN PROGRESS */}
    {step === 5 && <>
      <Separator size={160} />

      <div className="text-center">
        <h1 style={{ fontSize: "4rem" }}>
          <strong>{testProgress || 0}%</strong>
        </h1>

        <Separator size={20} />

        <div
          className="progress"
          aria-label="Animated striped example"
          role="progressbar">
            <div className={`progress-bar bg-success ${testProgress <= 99 && "progress-bar-animated progress-bar-striped"}`} style={{ width: `${testProgress || 0}%` }}></div>
        </div>

        <Separator size={20} />

        {testProgress < 100 && <>
          <strong>Test in Progress...</strong><br />
          <small className="text-muted">
            <i className="fas fa-clock me-2"></i> It will take around 30 minutes<br />
            You can close this page. The test will continue in the background and the result will be available on the home page.

            <br /><br />
            <span className="text-danger">
              <i>
                (this is a simulation, it will take only 1 minute)
              </i>
            </span>
          </small>
        </>}

        {testProgress > 99 && <>
          <strong>Test is Done</strong>
        </>}
      </div>

      <Separator size={60} />

      <div className="d-grid">
        <button
          onClick={() => setStep(6)}
          type="button"
          disabled={testProgress < 100}
          className="btn btn-primary btn-featured">
            View Result
        </button>
      </div>
    </>}



    {/* STEP 6 - TEST RESULT */}
    {step === 6 && <>
      <div className="text-center">
        <img src="/images/icon-result-negative.png" alt="" className="img-fluid" style={{ width:  "90px" }} />
        {/*
        <img src="/images/icon-result-positive.png" alt="" className="img-fluid" style={{ width:  "90px" }} />
        */}

        <Separator size={20} />

        <h2 className="negative">
          <strong>Negative</strong>
        </h2>
        {/*
        <h2 className="positive">
          <strong>Positive</strong>
        </h2>
        */}

        <Separator size={20} />

        <small className="text-muted">
          {dayjs().format("DD/MM/YYYY HH:mm:ss")}
        </small>

        <Separator size={10} />

        <strong>
          {selectedSensorType === "covid19" && "COVID 19"}
          {selectedSensorType === "covidflu" && "COVID / FLU"}
        </strong><br />
        <strong>Reader Serial Number:</strong> {selectedReader.serialNumber}<br />
        <strong>Patient Name:</strong> John Doe (?)<br />

        <Separator size={30} />

        <small className="text-muted">
          Help us track the spread of illness. Report your anonymous results to public health authorities (<a href="http://makemytestcount.org" target="_blank" rel="noopener noreferrer">MakeMyTestCount.Org</a>).
        </small>

        <Separator size={20} />
      </div>

      <div className="d-grid">
        <Link to={"/home"} className="btn btn-primary btn-featured">
          Report &amp; Finish
        </Link>

        <Separator size={20} />

        <Link to={"/home"} className="btn btn-primary btn-outline-featured">
          Just Finish
        </Link>
      </div>
    </>}
  </>
};

export default NewTest;